/* ==========================================================================
   Product Notes Editor
   Admin component for defining product notes (read-only label/value pairs
   visible to providers as informational metadata on products)
   ========================================================================== */

import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Plus, Trash2 } from 'lucide-react'
import type { ProductNoteDefinition } from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface ProductNotesEditorProps {
  value: ProductNoteDefinition[]
  onChange: (notes: ProductNoteDefinition[]) => void
  disabled?: boolean
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ProductNotesEditor({
  value,
  onChange,
  disabled = false,
}: ProductNotesEditorProps) {
  function addNote() {
    if (value.length >= 20) return
    onChange([...value, { label: '', value: '' }])
  }

  function removeNote(index: number) {
    onChange(value.filter((_, i) => i !== index))
  }

  function updateNote(index: number, updates: Partial<ProductNoteDefinition>) {
    onChange(
      value.map((note, i) => (i === index ? { ...note, ...updates } : note))
    )
  }

  // Check for duplicate labels
  const labels = value.map((n) => n.label.trim().toLowerCase()).filter(Boolean)
  const hasDuplicateLabels = labels.length !== new Set(labels).size

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[var(--color-charcoal)]">
          Product Notes
        </label>
        <span className="text-xs text-[var(--color-gray-500)]">
          {value.length}/20 notes
        </span>
      </div>

      {hasDuplicateLabels && (
        <p className="text-sm text-[var(--color-error)]">
          Duplicate note labels detected. Each note must have a unique label.
        </p>
      )}

      {value.length === 0 && (
        <p className="text-sm text-[var(--color-gray-500)]">
          No product notes defined. Add notes to display read-only information
          to providers (e.g., cost, storage instructions).
        </p>
      )}

      {value.map((note, index) => (
        <div key={index} className="flex items-start gap-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
            <Input
              type="text"
              value={note.label}
              onChange={(e) => updateNote(index, { label: e.target.value })}
              disabled={disabled}
              placeholder="Label (e.g., Cost)"
              error={!note.label.trim() && value.length > 0}
            />
            <Input
              type="text"
              value={note.value}
              onChange={(e) => updateNote(index, { value: e.target.value })}
              disabled={disabled}
              placeholder="Value (e.g., $25)"
              error={!note.value.trim() && value.length > 0}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeNote(index)}
            disabled={disabled}
            className="text-[var(--color-error)] hover:text-[var(--color-error)] mt-1"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {value.length < 20 && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addNote}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Product Note
        </Button>
      )}
    </div>
  )
}

export default ProductNotesEditor
